import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { X, ArrowLeft, ArrowRight, Sparkles } from 'lucide-react';

export const TourGuide = ({ steps, active, onClose }) => {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  const updateRect = useCallback(() => {
    const step = steps[idx];
    if (!step) return;
    const el = step.selector ? document.querySelector(step.selector) : null;
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setRect(null);
    }
  }, [idx, steps]);

  useEffect(() => {
    if (!active) return;
    const step = steps[idx];
    const el = step?.selector ? document.querySelector(step.selector) : null;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(updateRect, 350);
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [active, idx, updateRect, steps]);

  useEffect(() => {
    if (active) setIdx(0);
  }, [active]);

  if (!active || steps.length === 0) return null;

  const step = steps[idx];
  const isLast = idx === steps.length - 1;

  let tooltipStyle = { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' };
  if (rect) {
    const below = rect.top + rect.height + 210 < window.innerHeight;
    tooltipStyle = {
      top: below ? rect.top + rect.height + 14 : Math.max(16, rect.top - 210),
      left: Math.min(Math.max(rect.left, 16), Math.max(16, window.innerWidth - 372)),
    };
  }

  return (
    <div className="fixed inset-0 z-[100]" data-testid="tour-guide-overlay">
      <div className="absolute inset-0" onClick={onClose} />
      {rect ? (
        <div
          className="absolute rounded-xl transition-all duration-300 pointer-events-none"
          style={{
            top: rect.top - 6, left: rect.left - 6,
            width: rect.width + 12, height: rect.height + 12,
            boxShadow: '0 0 0 9999px rgba(2,6,23,0.72)',
            border: '2px solid rgb(147, 51, 234)',
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-slate-950/75 pointer-events-none" />
      )}

      <div
        className="absolute w-[350px] max-w-[calc(100vw-32px)] bg-white rounded-xl border border-slate-200 shadow-2xl p-5 animate-fade-up"
        style={tooltipStyle}
        data-testid="tour-tooltip"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-purple-700">
            <Sparkles className="w-3.5 h-3.5" /> Paso {idx + 1} de {steps.length}
          </span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors" data-testid="tour-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <h4 className="text-base font-bold text-slate-900 mb-1.5">{step.title}</h4>
        <p className="text-sm text-slate-600 leading-relaxed mb-4">{step.text}</p>
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {steps.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? 'bg-purple-600' : 'bg-slate-200'}`} />
            ))}
          </div>
          <div className="flex gap-2">
            {idx > 0 && (
              <Button size="sm" variant="outline" onClick={() => setIdx(idx - 1)} className="border-slate-200" data-testid="tour-prev-btn">
                <ArrowLeft className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => (isLast ? onClose() : setIdx(idx + 1))}
              className="bg-purple-600 hover:bg-purple-700 active:scale-95 transition-transform"
              data-testid="tour-next-btn"
            >
              {isLast ? 'Finalizar' : 'Siguiente'} {!isLast && <ArrowRight className="w-3.5 h-3.5 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
